import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/logger';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

interface CompleteContractDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    contractId: number;
    onSuccess?: () => void;
}

export default function CompleteContractDialog({
    open,
    onOpenChange,
    contractId,
    onSuccess,
}: CompleteContractDialogProps) {
    const queryClient = useQueryClient();
    const [reason, setReason] = useState('');
    // Completion date is required: overdue interest stops accruing as of this date, so the
    // dialog always sends one. Clicking the selected day in the picker deselects it
    // (react-day-picker single mode) — ignore that instead of silently dropping the date.
    const [completionDate, setCompletionDate] = useState<Date>(new Date());

    const completeMutation = useMutation({
        mutationFn: async () => {
            const { data, error } = await supabase.rpc('complete_contract', {
                contract_id_param: contractId,
                reason_param: reason.trim() || undefined,
                occurred_at_param: completionDate.toISOString(),
            });
            if (error) throw error;
            return data;
        },
        onSuccess: (result: any) => {
            if (result.success) {
                toast.success('Contract marked as complete');
                queryClient.invalidateQueries({ queryKey: ['contract-insights', String(contractId)] });
                queryClient.invalidateQueries({ queryKey: ['contracts'] });
                queryClient.invalidateQueries({ queryKey: ['contract-status-for-signing', contractId] });
                setReason('');
                setCompletionDate(new Date());
                onOpenChange(false);
                onSuccess?.();
            } else {
                toast.error(result.error || 'Failed to complete contract');
            }
        },
        onError: (error) => {
            captureError(error);
            toast.error('Failed to complete contract');
        },
    });

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        Mark Contract as Complete?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        This marks the contract as completed. Overdue interest stops accruing as of the
                        completion date.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="completion-date" className="text-sm font-medium">
                            Completion Date
                        </Label>
                        <DatePicker
                            date={completionDate}
                            setDate={(date) => { if (date) setCompletionDate(date); }}
                            placeholder="Select completion date"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="completion-reason" className="text-sm font-medium">
                            Reason <span className="text-muted-foreground font-normal">(optional)</span>
                        </Label>
                        <Textarea
                            id="completion-reason"
                            placeholder="Any notes about completing this contract?"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                        />
                    </div>
                </div>

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={completeMutation.isPending}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault();
                            completeMutation.mutate();
                        }}
                        disabled={completeMutation.isPending}
                        className="bg-green-600 hover:bg-green-700 text-white"
                    >
                        {completeMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : null}
                        Mark Complete
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
